import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { RevealText, FadeUp } from "@/components/site/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { t } = useLang();
  const [token, setToken] = useState(() => window.localStorage.getItem("sw-admin-token") || "");
  const [authed, setAuthed] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRequests = async (tk) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/service-requests`, { headers: { "X-Admin-Token": tk } });
      setRequests(res.data);
      setAuthed(true);
      window.localStorage.setItem("sw-admin-token", tk);
    } catch (e) {
      toast.error("Unauthorized");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchRequests(token); }, []); // eslint-disable-line

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/service-requests/${id}?status=${status}`, {}, { headers: { "X-Admin-Token": token } });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Updated");
    } catch { toast.error("Failed"); }
  };

  const deleteReq = async (id) => {
    try {
      await axios.delete(`${API}/service-requests/${id}`, { headers: { "X-Admin-Token": token } });
      setRequests((prev) => prev.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const logout = () => {
    window.localStorage.removeItem("sw-admin-token");
    setToken(""); setAuthed(false); setRequests([]);
  };

  return (
    <div data-testid="admin-page" className="pt-28 md:pt-36 pb-24 min-h-screen">
      <section className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-4">Admin</div>
        <RevealText lines={[t.admin.title]} as="h1" className="display text-4xl md:text-6xl text-white leading-tight" />

        {!authed ? (
          <FadeUp className="mt-12 max-w-md sw-card rounded-md p-8">
            <label className="block text-[11px] uppercase tracking-[0.3em] text-white/50 mb-3">{t.admin.tokenLabel}</label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="admin-token"
              className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--sw-orange)] outline-none py-3 text-white"
              placeholder="••••••••"
            />
            <button
              onClick={() => fetchRequests(token)}
              disabled={loading || !token}
              data-testid="admin-login"
              className="mt-6 btn-primary px-6 py-3 rounded-full text-[12px] uppercase tracking-[0.22em] font-semibold disabled:opacity-50"
            >
              {t.admin.login}
            </button>
          </FadeUp>
        ) : (
          <>
            <div className="mt-8 flex items-center gap-3">
              <button onClick={() => fetchRequests(token)} className="btn-ghost px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.2em]">Refresh</button>
              <button onClick={logout} className="btn-ghost px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.2em]" data-testid="admin-logout">{t.admin.logout}</button>
            </div>
            {requests.length === 0 ? (
              <div className="mt-16 text-white/60">{t.admin.empty}</div>
            ) : (
              <div className="mt-10 space-y-4" data-testid="admin-requests">
                {requests.map((r) => (
                  <div key={r.id} className="sw-card rounded-md p-6 md:p-8">
                    <div className="flex flex-wrap items-start justify-between gap-6">
                      <div className="min-w-0">
                        <div className="display text-2xl text-white">{r.name}</div>
                        <div className="text-white/60 text-sm mt-1">{r.phone} · {r.email || "—"} · {r.service || "—"}</div>
                        <p className="text-white/80 mt-4 max-w-2xl">{r.message}</p>
                        <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-white/40">{r.language} · {new Date(r.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <select
                          value={r.status}
                          onChange={(e) => updateStatus(r.id, e.target.value)}
                          className="bg-[color:var(--sw-bg)] border border-white/15 text-white text-sm rounded-md px-3 py-2"
                        >
                          <option value="new">{t.admin.status.new}</option>
                          <option value="in_progress">{t.admin.status.in_progress}</option>
                          <option value="done">{t.admin.status.done}</option>
                          <option value="archived">{t.admin.status.archived}</option>
                        </select>
                        <button onClick={() => deleteReq(r.id)} className="text-[11px] uppercase tracking-[0.24em] text-red-400 hover:text-red-300">Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default Admin;
